import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  BarChart3,
  ChevronDown,
  RefreshCw,
  Sparkles,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import {
  CartesianGrid,
  Line,
  LineChart,
  PolarAngleAxis,
  RadialBar,
  RadialBarChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import toast from 'react-hot-toast';
import { qcAPI } from '../../services/api';

const ZONE_COLORS = {
  inRange: '#16a34a',
  warning: '#f59e0b',
  critical: '#dc2626',
};

function getZScore(point) {
  if (typeof point?.z_score === 'number') return point.z_score;
  const mean = Number(point?.mean || 0);
  const sd = Number(point?.plus1 || 0) - mean;
  if (!sd) return 0;
  return Number((((Number(point?.result || 0) - mean) / sd) || 0).toFixed(2));
}

function getPointSeverity(point, rejectionThresholdSd = 3) {
  const inRangeMax = rejectionThresholdSd === 2 ? 1 : 2;
  const z = Math.abs(getZScore(point));
  if (z < inRangeMax) return 'inRange';
  if (z < rejectionThresholdSd) return 'warning';
  return 'critical';
}

function getPassRateTrend(points, rejectionThresholdSd = 3) {
  if (!Array.isArray(points) || points.length < 4) return { delta: 0, up: true };
  const mid = Math.floor(points.length / 2);
  const first = points.slice(0, mid);
  const second = points.slice(mid);
  const firstPass = first.length
    ? (first.filter((p) => getPointSeverity(p, rejectionThresholdSd) === 'inRange').length / first.length) * 100
    : 0;
  const secondPass = second.length
    ? (second.filter((p) => getPointSeverity(p, rejectionThresholdSd) === 'inRange').length / second.length) * 100
    : 0;
  const delta = Number((secondPass - firstPass).toFixed(1));
  return { delta, up: delta >= 0 };
}

const WESTGARD_RULE_INFO = {
  '1-3s': { label: '1-3s', severity: 'rejected', desc: 'قراءة تجاوزت ±3SD' },
  '1-2s': { label: '1-2s', severity: 'warning', desc: 'قراءة تجاوزت ±2SD (تحذير)' },
  '2-2s': { label: '2-2s', severity: 'rejected', desc: 'قراءتان متتاليتان تجاوزتا ±2SD بنفس الاتجاه' },
  'R-4s': { label: 'R-4s', severity: 'rejected', desc: 'الفرق بين قراءتين ≥ 4SD' },
  '4-1s': { label: '4-1s', severity: 'rejected', desc: '4 قراءات متتالية تجاوزت ±1SD بنفس الاتجاه' },
  '10x':  { label: '10x',  severity: 'rejected', desc: '10 قراءات في نفس جانب المتوسط' },
  '7-T':  { label: '7-T',  severity: 'rejected', desc: '7 قراءات في اتجاه تصاعدي أو تنازلي' },
};

function QCDashboard() {
  const [loading, setLoading] = useState(true);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [data, setData] = useState({
    controls: [],
    tests: [],
    selectedControlId: '',
    selectedTestId: '',
    summary: { total: 0, pass: 0, fail: 0 },
    alerts: [],
    chart: [],
  });
  const [filters, setFilters] = useState({
    control_id: '',
    lab_test_id: '',
    from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10),
    to: new Date().toISOString().slice(0, 10),
  });

  const loadDashboard = async (paramsInput) => {
    setLoading(true);
    try {
      const params = paramsInput || filters;
      const response = await qcAPI.getDashboard(params);
      const nextData = response.data?.data || {};
      setData(nextData);
      setSelectedPoint(null);
      setFilters((prev) => {
        const next = {
          ...prev,
          ...params,
          control_id: nextData.selectedControlId || params.control_id || prev.control_id,
          lab_test_id: nextData.selectedTestId || params.lab_test_id || prev.lab_test_id,
        };
        return next;
      });
    } catch (error) {
      toast.error('تعذر تحميل لوحة مراقبة الجودة');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFilterChange = (key, value) => {
    const next = { ...filters, [key]: value };
    if (key === 'control_id') {
      next.lab_test_id = '';
    }
    setFilters(next);
    loadDashboard(next);
  };

  const activeTests = useMemo(() => data.tests || [], [data.tests]);
  const chartData = useMemo(() => data.chart || [], [data.chart]);
  const selectedControl = useMemo(
    () => (data.controls || []).find((c) => c._id === (data.selectedControlId || filters.control_id)) || null,
    [data.controls, data.selectedControlId, filters.control_id]
  );
  const rejectionThresholdSd = useMemo(
    () => (selectedControl?.critical_rejection_threshold === '2sd' ? 2 : 3),
    [selectedControl]
  );
  const inRangeMax = rejectionThresholdSd === 2 ? 1 : 2;

  const metrics = useMemo(() => {
    const total = chartData.length;
    const accepted = chartData.filter((p) => p.westgard_status === 'accepted' || (!p.westgard_status && p.status === 'pass')).length;
    const warning = chartData.filter((p) => p.westgard_status === 'warning').length;
    const rejected = chartData.filter((p) => p.westgard_status === 'rejected' || p.status === 'fail').length;
    const passRate = total ? Number(((accepted / total) * 100).toFixed(1)) : 0;
    const trend = getPassRateTrend(chartData, rejectionThresholdSd);

    const warningPenalty = total ? (warning / total) * 30 : 0;
    const rejectedPenalty = total ? (rejected / total) * 70 : 0;
    const stabilityIndex = Math.max(0, Math.min(100, Number((100 - warningPenalty - rejectedPenalty).toFixed(1))));

    return { total, accepted, warning, rejected, passRate, trend, stabilityIndex };
  }, [chartData, rejectionThresholdSd]);

  const westgardInsights = useMemo(() => {
    const backend = data.westgard;
    if (backend && backend.violations && backend.violations.length > 0) {
      return backend.violations.map(v => ({
        rule: v.rule,
        severity: v.severity,
        message: v.message,
        info: WESTGARD_RULE_INFO[v.rule] || null,
      }));
    }
    return [{ rule: null, severity: 'ok', message: 'لا توجد انتهاكات لقواعد Westgard ضمن الفترة المحددة' }];
  }, [data.westgard]);

  const tasks = useMemo(() => {
    const base = chartData
      .map((point) => {
        const z = getZScore(point);
        const wStatus = point.westgard_status || 'accepted';
        const wRules = point.westgard_rules || [];
        const needsAction = wStatus === 'rejected' || (wStatus === 'warning' && !point.corrective_action);
        return {
          ...point,
          z,
          wStatus,
          wRules,
          needsAction,
          taskStatus: point.corrective_action ? 'مكتمل' : needsAction ? 'يتطلب متابعة' : 'للمراجعة',
        };
      })
      .filter((point) => point.wStatus !== 'accepted');

    if (!selectedPoint) return base;
    return base.filter((item) => item.run_id === selectedPoint.run_id);
  }, [chartData, selectedPoint]);

  const chartDomain = useMemo(() => {
    if (!chartData.length) return [0, 1];
    const mean = Number(chartData[0]?.mean || 0);
    const plus3 = Number(chartData[0]?.plus3 || mean + 3);
    const minus3 = Number(chartData[0]?.minus3 || mean - 3);
    const minResult = Math.min(...chartData.map((d) => Number(d.result)));
    const maxResult = Math.max(...chartData.map((d) => Number(d.result)));
    const low = Math.min(minus3 - Math.abs(plus3 - mean), minResult);
    const high = Math.max(plus3 + Math.abs(plus3 - mean), maxResult);
    return [Number((low - 0.5).toFixed(2)), Number((high + 0.5).toFixed(2))];
  }, [chartData]);

  const chartRange = useMemo(() => {
    if (!chartData.length) return { x1: '', x2: '' };
    return { x1: chartData[0].date, x2: chartData[chartData.length - 1].date };
  }, [chartData]);

  const renderDot = (props) => {
    const { cx, cy, payload } = props;
    const wStatus = payload?.westgard_status || 'accepted';
    const fill = wStatus === 'rejected' ? ZONE_COLORS.critical
      : wStatus === 'warning' ? ZONE_COLORS.warning
      : ZONE_COLORS.inRange;
    const isSelected = selectedPoint?.run_id === payload?.run_id;
    return (
      <circle
        cx={cx}
        cy={cy}
        r={isSelected ? 6 : 4}
        fill={fill}
        stroke="#fff"
        strokeWidth={isSelected ? 2.5 : 1.5}
        style={{ cursor: 'pointer' }}
        onClick={() => setSelectedPoint(payload)}
      />
    );
  };

  const customTooltip = ({ active, payload }) => {
    if (!active || !payload?.[0]?.payload) return null;
    const point = payload[0].payload;
    const z = getZScore(point);
    const wStatus = point.westgard_status || 'accepted';
    const wRules = point.westgard_rules || [];
    return (
      <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-3 text-xs min-w-[200px]">
        <p className="font-semibold text-slate-800 mb-1">{point.date}</p>
        <p className="text-slate-600">النتيجة: <span className="font-semibold">{point.result}</span></p>
        <p className="text-slate-600">Z-Score: <span className="font-semibold">{z}</span></p>
        <div className="mt-1.5 flex items-center gap-1.5">
          <span className="text-slate-600">Westgard:</span>
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
            wStatus === 'rejected' ? 'bg-rose-100 text-rose-700'
            : wStatus === 'warning' ? 'bg-amber-100 text-amber-700'
            : 'bg-emerald-100 text-emerald-700'
          }`}>
            {wStatus === 'rejected' ? 'Rejected' : wStatus === 'warning' ? 'Warning' : 'Accepted'}
          </span>
        </div>
        {wRules.length > 0 && (
          <div className="mt-1 flex gap-1 flex-wrap">
            {wRules.map((r, i) => (
              <span key={i} className="text-[10px] px-1 py-0.5 bg-slate-100 rounded font-mono">{r}</span>
            ))}
          </div>
        )}
        {point.corrective_action && (
          <>
            <p className="text-slate-600 mt-1.5">الإجراء التصحيحي:</p>
            <p className="text-slate-800 font-medium">{point.corrective_action}</p>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-[#1E293B] flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-slate-100" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#1E293B]">لوحة المراقبة والتقارير</h1>
                <p className="text-sm text-slate-500 mt-1">متابعة استقرار الجودة عبر مخطط Levey-Jennings</p>
              </div>
            </div>
            <button
              onClick={() => loadDashboard(filters)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 hover:bg-slate-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              تحديث
            </button>
          </div>
        </motion.div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">اختر الكنترول</label>
              <select
                value={filters.control_id}
                onChange={(e) => onFilterChange('control_id', e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl bg-white"
              >
                <option value="">اختر...</option>
                {(data.controls || []).map((control) => (
                  <option key={control._id} value={control._id}>
                    {control.control_name} - {control.lot_number}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">اختر التحليل</label>
              <select
                value={filters.lab_test_id}
                onChange={(e) => onFilterChange('lab_test_id', e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl bg-white"
              >
                <option value="">اختر...</option>
                {activeTests.map((test) => (
                  <option key={test.lab_test_id} value={test.lab_test_id}>
                    {test.test_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">من تاريخ</label>
              <input
                type="date"
                value={filters.from}
                onChange={(e) => onFilterChange('from', e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">إلى تاريخ</label>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={filters.to}
                  onChange={(e) => onFilterChange('to', e.target.value)}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-xl"
                />
                <button
                  onClick={() => loadDashboard(filters)}
                  className="px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700"
                >
                  تطبيق
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-2xl border border-emerald-300 shadow-sm p-4">
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-xs text-emerald-700">Pass Rate</p>
              <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                {metrics.trend.up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                {Math.abs(metrics.trend.delta)}%
              </span>
            </div>
            <p className="text-3xl font-extrabold text-emerald-700">{metrics.passRate}%</p>
            <p className="text-xs text-slate-500 mt-1">قراءات ضمن النطاق</p>
          </div>

          <div className="bg-white rounded-2xl border border-amber-300 shadow-sm p-4">
            <p className="text-xs text-amber-700 mb-1.5">Warning Tests</p>
            <p className="text-3xl font-extrabold text-amber-700">{metrics.warning}</p>
            <p className="text-xs text-slate-500 mt-1">
              {rejectionThresholdSd === 2 ? 'تحذير (بين 1SD و 2SD)' : 'تحذير (بين 2SD و 3SD)'}
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-rose-300 shadow-sm p-4">
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-xs text-rose-700">Westgard Rejected</p>
              <AlertCircle className="w-4 h-4 text-rose-600" />
            </div>
            <p className="text-3xl font-extrabold text-rose-700">{metrics.rejected}</p>
            <p className="text-xs text-slate-500 mt-1">رفض بسبب قواعد Westgard</p>
          </div>

          <div className="bg-white rounded-2xl border border-purple-300 shadow-sm p-4">
            <p className="text-xs text-purple-700 mb-1.5">Machine Stability Index</p>
            <div className="h-[82px] flex items-center justify-between">
              <ResponsiveContainer width="55%" height="100%">
                <RadialBarChart
                  innerRadius="68%"
                  outerRadius="100%"
                  data={[{ name: 'index', value: metrics.stabilityIndex }]}
                  startAngle={180}
                  endAngle={0}
                >
                  <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                  <RadialBar dataKey="value" fill="#7c3aed" cornerRadius={8} />
                </RadialBarChart>
              </ResponsiveContainer>
              <p className="text-3xl font-extrabold text-purple-700">{metrics.stabilityIndex}%</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 mb-6">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-blue-600" />
                <h3 className="text-sm font-semibold text-slate-700">Levey-Jennings Chart</h3>
              </div>
              {selectedPoint && (
                <button
                  onClick={() => setSelectedPoint(null)}
                  className="text-xs px-2.5 py-1 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                >
                  إلغاء تحديد النقطة
                </button>
              )}
            </div>
            <div className="h-[440px]">
              {loading ? (
                <div className="h-full flex items-center justify-center text-slate-500">جاري التحميل...</div>
              ) : !chartData.length ? (
                <div className="h-full flex items-center justify-center text-slate-500">
                  لا توجد قراءات ضمن الفترة المحددة
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis domain={chartDomain} />
                    <Tooltip content={customTooltip} />

                    {/* Dynamic zones based on control rejection threshold */}
                    <ReferenceArea
                      x1={chartRange.x1}
                      x2={chartRange.x2}
                      y1={chartData[0]?.mean - inRangeMax * (chartData[0]?.plus1 - chartData[0]?.mean)}
                      y2={chartData[0]?.mean + inRangeMax * (chartData[0]?.plus1 - chartData[0]?.mean)}
                      fill="#22c55e"
                      fillOpacity={0.12}
                    />
                    <ReferenceArea
                      x1={chartRange.x1}
                      x2={chartRange.x2}
                      y1={chartData[0]?.mean + inRangeMax * (chartData[0]?.plus1 - chartData[0]?.mean)}
                      y2={chartData[0]?.mean + rejectionThresholdSd * (chartData[0]?.plus1 - chartData[0]?.mean)}
                      fill="#facc15"
                      fillOpacity={0.16}
                    />
                    <ReferenceArea
                      x1={chartRange.x1}
                      x2={chartRange.x2}
                      y1={chartData[0]?.mean - rejectionThresholdSd * (chartData[0]?.plus1 - chartData[0]?.mean)}
                      y2={chartData[0]?.mean - inRangeMax * (chartData[0]?.plus1 - chartData[0]?.mean)}
                      fill="#facc15"
                      fillOpacity={0.16}
                    />
                    <ReferenceArea
                      x1={chartRange.x1}
                      x2={chartRange.x2}
                      y1={chartData[0]?.mean + rejectionThresholdSd * (chartData[0]?.plus1 - chartData[0]?.mean)}
                      y2={chartDomain[1]}
                      fill="#f87171"
                      fillOpacity={0.14}
                    />
                    <ReferenceArea
                      x1={chartRange.x1}
                      x2={chartRange.x2}
                      y1={chartDomain[0]}
                      y2={chartData[0]?.mean - rejectionThresholdSd * (chartData[0]?.plus1 - chartData[0]?.mean)}
                      fill="#f87171"
                      fillOpacity={0.14}
                    />

                    <ReferenceLine y={chartData[0]?.mean} stroke="#0f172a" strokeWidth={2} />
                    <ReferenceLine y={chartData[0]?.plus1} stroke="#16a34a" strokeDasharray="6 4" />
                    <ReferenceLine y={chartData[0]?.minus1} stroke="#16a34a" strokeDasharray="6 4" />
                    <ReferenceLine y={chartData[0]?.plus2} stroke="#f59e0b" strokeDasharray="6 4" />
                    <ReferenceLine y={chartData[0]?.minus2} stroke="#f59e0b" strokeDasharray="6 4" />
                    <ReferenceLine y={chartData[0]?.plus3} stroke="#dc2626" strokeDasharray="6 4" />
                    <ReferenceLine y={chartData[0]?.minus3} stroke="#dc2626" strokeDasharray="6 4" />

                    <Line
                      type="monotone"
                      dataKey="result"
                      stroke="#334155"
                      strokeWidth={2}
                      dot={renderDot}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          <div className="xl:col-span-7 bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-purple-600" />
              <h3 className="text-sm font-semibold text-slate-700">AI Insight Panel - Westgard</h3>
            </div>
            <div className="space-y-2">
              {westgardInsights.map((insight, idx) => (
                <div
                  key={`insight-${idx}`}
                  className={`p-3 rounded-xl border text-sm ${
                    insight.severity === 'ok'
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                      : insight.severity === 'rejected'
                      ? 'bg-rose-50 border-rose-200 text-rose-900'
                      : 'bg-amber-50 border-amber-200 text-amber-900'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {insight.rule && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${
                        insight.severity === 'rejected' ? 'bg-rose-200 text-rose-800' : 'bg-amber-200 text-amber-800'
                      }`}>
                        {insight.rule}
                      </span>
                    )}
                    <span>{insight.message}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <h4 className="text-sm font-semibold text-slate-700">التنبيهات الذكية</h4>
              </div>
              {(data.alerts || []).length === 0 ? (
                <p className="text-sm text-slate-500">لا توجد حالات فشل حديثة.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {data.alerts.map((alert, idx) => (
                    <div key={`${alert.test_id}-${idx}`} className="p-2.5 rounded-lg bg-amber-50 border border-amber-200">
                      <p className="text-xs font-medium text-amber-900">{alert.message}</p>
                      <p className="text-[11px] text-amber-700 mt-1">{alert.date}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="xl:col-span-5 bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-700">My Tasks Panel</h3>
              <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600">
                {tasks.length} مهام
              </span>
            </div>

            {selectedPoint && (
              <div className="mb-3 p-2.5 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-700 flex items-center gap-1.5">
                <ChevronDown className="w-3.5 h-3.5 rotate-90" />
                تم تصفية المهام حسب النقطة المختارة بتاريخ {selectedPoint.date}
              </div>
            )}

            {tasks.length === 0 ? (
              <p className="text-sm text-slate-500">لا توجد مهام متابعة ضمن الفلاتر الحالية.</p>
            ) : (
              <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                {tasks.map((task, idx) => {
                  const isRejected = task.wStatus === 'rejected';
                  const isWarning = task.wStatus === 'warning';
                  return (
                    <div
                      key={`${task.run_id}-${idx}`}
                      className={`p-3 rounded-xl border ${
                        isRejected
                          ? 'border-rose-200 bg-rose-50'
                          : isWarning
                          ? 'border-amber-200 bg-amber-50'
                          : 'border-slate-200 bg-white'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-slate-800">{task.date}</p>
                          <p className="text-xs text-slate-600 mt-0.5">
                            النتيجة: {task.result} | Z: {task.z}
                          </p>
                          {task.wRules.length > 0 && (
                            <div className="flex gap-1 mt-1 flex-wrap">
                              {task.wRules.map((rule, ri) => (
                                <span key={ri} className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                                  isRejected ? 'bg-rose-200 text-rose-800' : 'bg-amber-200 text-amber-800'
                                }`}>
                                  {rule}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <span
                          className={`text-[11px] px-2 py-0.5 rounded-full ${
                            isRejected
                              ? 'bg-rose-100 text-rose-700'
                              : isWarning
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-slate-100 text-slate-700'
                          }`}
                        >
                          {task.taskStatus}
                        </span>
                      </div>
                      <p className="text-xs text-slate-700 mt-2">
                        الإجراء التصحيحي: {task.corrective_action || 'لم يتم إضافة إجراء بعد'}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default QCDashboard;
